import { useRef } from "react";
import { useScroll, useTransform, motion } from "framer-motion";
import { Link } from "react-router-dom";

const cards = [
  {
    title: "Custom software, built for you.",
    description:
      "Off-the-shelf tools don't fit every business. We design and build custom software tailored to your exact needs — from web and mobile applications to complex enterprise systems. Our team handles everything: architecture, design, development, QA, and launch.",
    cta: "Our Services",
    ctaLink: "/our-services",
    color: "#0073E5",
    textColor: "text-white",
    btnColor: "bg-white text-black hover:bg-gray-100",
    labelColor: "rgba(255,255,255,0.5)",
    illustration: "https://readdy.ai/api/search-image?query=software%20developer%20coding%20at%20desk%20with%20multiple%20monitors%20showing%20code%20clean%20modern%20office%20bright%20background%20professional%20atmosphere%20high%20quality&width=800&height=900&seq=feat-card-01&orientation=portrait",
  },
  {
    title: "Strategy before code.",
    description:
      "Great products start with great decisions. Our IT consultants help you choose the right technologies, optimize your processes, and avoid costly mistakes. We assess your current setup, identify bottlenecks, and define a clear roadmap — so your investment goes exactly where it should.",
    cta: "IT Consulting",
    ctaLink: "/our-services",
    color: "#D4F419",
    textColor: "text-black",
    btnColor: "bg-black text-white hover:bg-gray-800",
    labelColor: "rgba(0,0,0,0.4)",
    illustration: "https://readdy.ai/api/search-image?query=business%20consultant%20presenting%20strategy%20on%20whiteboard%20in%20modern%20conference%20room%20clean%20minimal%20office%20background%20professional%20team%20meeting&width=800&height=900&seq=feat-card-02&orientation=portrait",
  },
  {
    title: "Marketing that actually converts.",
    description:
      "Building the product is only half the journey. We craft data-driven digital marketing strategies that reach your target audience, increase brand visibility, and drive real user growth. From SEO and content to paid campaigns and social media — we cover it all.",
    cta: "Digital Marketing",
    ctaLink: "/our-services",
    color: "#E4D6EA",
    textColor: "text-black",
    btnColor: "bg-black text-white hover:bg-gray-800",
    labelColor: "rgba(0,0,0,0.4)",
    illustration: "https://readdy.ai/api/search-image?query=digital%20marketing%20analytics%20dashboard%20on%20screen%20charts%20graphs%20growth%20metrics%20modern%20workspace%20clean%20light%20background%20professional&width=800&height=900&seq=feat-card-03&orientation=portrait",
  },
  {
    title: "From idea to launch — and beyond.",
    description:
      "We partner with you through every stage: discovery, design, development, testing, launch, and post-launch support. You get a dedicated team of project managers, developers, designers, marketers, and QA engineers — all aligned to your goals from day one.",
    cta: "Our Products",
    ctaLink: "/our-products",
    color: "#F4971E",
    textColor: "text-black",
    btnColor: "bg-black text-white hover:bg-gray-800",
    labelColor: "rgba(0,0,0,0.4)",
    illustration: "https://readdy.ai/api/search-image?query=product%20launch%20team%20celebrating%20in%20modern%20office%20diverse%20group%20of%20professionals%20laptops%20bright%20clean%20background%20tech%20startup%20atmosphere&width=800&height=900&seq=feat-card-04&orientation=portrait",
  },
];

function StickyCard({
  card,
  index,
  total,
  containerRef,
}: {
  card: typeof cards[0];
  index: number;
  total: number;
  containerRef: React.RefObject<HTMLDivElement>;
}) {
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  const segmentSize = 1 / total;
  const start = index * segmentSize;
  const end = (index + 1) * segmentSize;

  const scale = useTransform(scrollYProgress, [start, end], [0.92, 1]);
  const y = useTransform(scrollYProgress, [start, end], ["100vh", "0vh"]);

  return (
    <motion.div
      className="sticky top-0 w-full h-screen flex items-center justify-center overflow-hidden"
      style={{
        zIndex: index + 1,
        y: index === 0 ? 0 : y,
        scale: index === 0 ? 1 : scale,
        backgroundColor: card.color,
      }}
    >
      <div className="w-full h-full flex flex-col md:flex-row">
        {/* Text side */}
        <div
          className={`flex-1 flex flex-col justify-center px-16 md:px-24 lg:px-32 py-16 ${card.textColor}`}
        >
          <span
            className="text-xs font-semibold uppercase tracking-widest mb-6"
            style={{ color: card.labelColor }}
          >
            0{index + 1} / 0{total}
          </span>
          <h3 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-8">
            {card.title}
          </h3>
          <p className="text-base md:text-lg leading-relaxed opacity-80 max-w-lg mb-10">
            {card.description}
          </p>
          <div>
            <Link
              to={card.ctaLink}
              className={`inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-sm transition-colors duration-200 cursor-pointer whitespace-nowrap ${card.btnColor}`}
            >
              {card.cta}
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        {/* Image side */}
        <div className="hidden md:block w-[40%] lg:w-[45%] h-full flex-shrink-0 overflow-hidden">
          <img
            src={card.illustration}
            alt={card.title}
            className="w-full h-full object-cover object-top"
          />
        </div>
      </div>
    </motion.div>
  );
}

export default function FeatureCards() {
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <section style={{ backgroundColor: "#FEF7EB" }}>
      {/* Section header */}
      <div className="py-16 px-8 md:px-32 max-w-none">
        <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: "#0073E5" }}>
          Why London App Syndicate
        </p>
        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight max-w-2xl">
          Technology and marketing, unified.
        </h2>
      </div>

      {/* Sticky card stack container */}
      <div
        ref={containerRef}
        style={{ height: `${cards.length * 100}vh` }}
        className="relative"
      >
        {cards.map((card, i) => (
          <StickyCard
            key={card.title}
            card={card}
            index={i}
            total={cards.length}
            containerRef={containerRef as React.RefObject<HTMLDivElement>}
          />
        ))}
      </div>
    </section>
  );
}
